/**
 * SelectPipeline — pick the check to run.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * Rebuilt twice. First from a version whose own header described the goal as
 * "Aceternity-inspired glassmorphism" and "stunning visual effects": five
 * categorical gradients (spending emerald and amber, the pass and review
 * hues, on navigation), a `role="article"` <div> with tabIndex={0} wrapping a
 * button to the same route, and a "Quick Stats" bar reporting "Success Rate
 * 98.7%" from a hardcoded literal that was never measured.
 *
 * Then trimmed to this. The first rebuild kept a sentence of description and
 * three feature bullets per card — about 90 words on a page whose entire job
 * is routing someone who already knows which pipeline they want. Recognition
 * needs a name, not a paragraph. The page is now names and icons; each
 * pipeline explains itself on its own setup screen, at the point where the
 * explanation is actually useful.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Ruler, Package, Grid3x3, Sparkles, BarChart3, ChevronRight } from 'lucide-react';

interface PipelineLink {
  id: string;
  title: string;
  icon: React.ElementType;
  route: string;
}

const PIPELINES: PipelineLink[] = [
  { id: 'dimensions', title: 'Dimensions', icon: Ruler, route: '/pipelines/dimensions' },
  { id: 'material', title: 'Material', icon: Package, route: '/pipelines/material' },
  { id: 'part-category', title: 'Part Category', icon: Grid3x3, route: '/pipelines/part-category' },
  { id: 'generic', title: 'Generic', icon: Sparkles, route: '/pipelines/generic' },
  { id: 'analytics', title: 'Analytics', icon: BarChart3, route: '/analytics' },
];

const SelectPipeline: React.FC = () => {
  return (
    <div className="mx-auto max-w-3xl">
      {/* h2, not h1 — the shell heading names the page. */}
      <h2 className="sr-only">Choose a pipeline</h2>

      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-bg-surface">
        {PIPELINES.map((pipeline) => (
          <li key={pipeline.id}>
            {/* One interactive element per row, so the pointer target and the
                tab stop are the same thing. */}
            <Link
              to={pipeline.route}
              data-testid={`pipeline-card-${pipeline.id}`}
              className="flex items-center gap-3 px-4 py-3.5 transition-colors duration-150 hover:bg-bg-subtle"
            >
              <pipeline.icon className="h-5 w-5 shrink-0 text-text-secondary" aria-hidden="true" />
              <span className="flex-1 font-medium text-text-primary">{pipeline.title}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SelectPipeline;
